import {Component, OnInit, OnDestroy, TemplateRef} from '@angular/core';
import {Subject} from 'rxjs/Subject';
import {Subscription} from 'rxjs/Subscription';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Router} from '@angular/router';
import {Api} from '../../providers/api';
import {Auth} from '../../providers/auth';
import {MaintainerCircuit, PaymentHistoryEntry} from '../../providers/models';
import * as moment from 'moment';

@Component({
  selector: 'app-maintainer-console',
  templateUrl: './maintainer-console.component.html',
  styleUrls: ['./maintainer-console.component.css']
})
export class MaintainerConsoleComponent implements OnInit, OnDestroy {
  circuits: MaintainerCircuit[] = [];
  total = 0;
  page = 1;
  limit = 20;
  q = '';
  statusFilter = '';
  providerFilter = '';
  billingFilter = '';
  loading = false;
  errorMsg: string = null;

  // Payments history
  historyEntries: PaymentHistoryEntry[] = [];
  historyTotal = 0;
  historyPage = 1;
  historyLimit = 20;
  historyGuid: string = null;
  loadingHistory = false;

  // Search debounce
  private searchSubject: Subject<string> = new Subject<string>();
  private searchSub: Subscription = null;

  constructor(private api: Api, private auth: Auth, private router: Router, private modalService: NgbModal) {}

  ngOnInit(): void {
    var self = this;
    var start = function(): void {
      self.searchSub = self.searchSubject.debounceTime(300).subscribe(function(val: string) {
        self.q = val;
        self.page = 1;
        self.loadCircuits();
      });
      // Initial load with defaults: page 1, empty filters = all
      self.page = 1;
      self.q = self.q || '';
      self.statusFilter = self.statusFilter || '';
      self.providerFilter = self.providerFilter || '';
      self.billingFilter = self.billingFilter || '';
      self.loadCircuits();
    };
    // Hash-routing maintainer 500ms: avoid 401 spam without token.
    // Auth restores user async from storage, so retry once before giving up.
    var hasToken = !!(this.auth && ((this.auth as any).token || (this.auth.user && this.auth.user.token)));
    if (!hasToken) {
      setTimeout(function() {
        var stillNoToken = !(self.auth && ((self.auth as any).token || (self.auth.user && self.auth.user.token)));
        if (stillNoToken) {
          self.router.navigate(['/login']);
        } else {
          start();
        }
      }, 500);
      return;
    }
    start();
  }

  ngOnDestroy(): void {
    if (this.searchSub) { this.searchSub.unsubscribe(); }
  }

  onSearchChange(val: string): void {
    this.searchSubject.next(val);
  }

  onFilterChange(): void {
    this.page = 1;
    this.loadCircuits();
  }

  loadCircuits(): void {
    var self = this;
    this.loading = true;
    this.errorMsg = null;
    var params: any = {page: this.page, limit: this.limit};
    if (this.q) { params.q = this.q; }
    if (this.statusFilter) { params.status = this.statusFilter; }
    if (this.providerFilter) { params.provider = this.providerFilter; }
    if (this.billingFilter) { params.billing = this.billingFilter; }
    this.api.getMaintainerCircuits(params).subscribe(function(res: any) {
      var body = res && res.data ? res.data : res;
      var list: any = null;
      if (res && res.data) { list = res.data; }
      else if (body && body.circuits) { list = body.circuits; }
      else if (body && Array.isArray(body)) { list = body; }
      else { list = (res && res.circuits) ? res.circuits : []; }
      // Api already normalizes to {data, circuits, total, page, limit}
      if (res && res.data && Array.isArray(res.data)) {
        self.circuits = res.data;
        self.total = res.total || 0;
        self.page = res.page || self.page;
      } else if (body && body.circuits) {
        self.circuits = body.circuits;
        self.total = body.total || 0;
      } else if (res && res.circuits) {
        self.circuits = res.circuits;
        self.total = res.total || 0;
      } else {
        self.circuits = list || [];
        self.total = (res && res.total) ? res.total : (body && body.total ? body.total : 0);
      }
      self.loading = false;
    }, function(err: any) {
      self.loading = false;
      self.errorMsg = err && err.error && err.error.message ? err.error.message : 'Error al cargar circuitos';
      if (!self.circuits) { self.circuits = []; }
    });
  }

  onPageChange(page: number): void {
    this.page = page;
    this.loadCircuits();
  }

  getStatusClass(status: string): string {
    if (status === 'LIFETIME') { return 'badge badge-warning'; }
    if (status === 'ACTIVE') { return 'badge badge-success'; }
    if (status === 'EXPIRED') { return 'badge badge-danger'; }
    if (status === 'TRIAL') { return 'badge badge-primary'; }
    return 'badge badge-secondary';
  }

  formatDate(dateStr: string): string {
    if (!dateStr) { return '-'; }
    // Use moment if available, fallback to Date pipe logic
    try {
      var m = moment(dateStr);
      if (m && typeof m.format === 'function' && m.isValid()) {
        return m.format('DD/MM/YYYY');
      }
    } catch (e) {}
    var d = new Date(dateStr);
    if (isNaN(d.getTime())) { return dateStr; }
    var dd = ('0' + d.getDate()).slice(-2);
    var mm = ('0' + (d.getMonth() + 1)).slice(-2);
    var yyyy = d.getFullYear();
    return dd + '/' + mm + '/' + yyyy;
  }

  // History — fetch first then open modal, use close() not dismissAll
  openHistory(guid: string, template: TemplateRef<any>): void {
    var self = this;
    this.historyGuid = guid;
    this.historyPage = 1;
    this.loadingHistory = true;
    this.historyEntries = [];
    this.api.getMaintainerPayments(this.historyGuid, this.historyPage, this.historyLimit).subscribe(function(res: any) {
      if (res && res.data && Array.isArray(res.data)) {
        self.historyEntries = res.data;
        self.historyTotal = res.total || 0;
      } else {
        var body = res && res.data ? res.data : res;
        if (body && body.payments) {
          self.historyEntries = body.payments;
          self.historyTotal = body.total || 0;
        } else {
          self.historyEntries = [];
          self.historyTotal = 0;
        }
      }
      self.loadingHistory = false;
      self.modalService.open(template, {size: 'lg', windowClass: 'modal-dialog-centered history-modal-wide'});
    }, function() {
      self.loadingHistory = false;
      self.historyEntries = [];
      self.modalService.open(template, {size: 'lg', windowClass: 'modal-dialog-centered history-modal-wide'});
    });
  }

  loadHistory(): void {
    var self = this;
    if (!this.historyGuid) { return; }
    this.loadingHistory = true;
    this.api.getMaintainerPayments(this.historyGuid, this.historyPage, this.historyLimit).subscribe(function(res: any) {
      if (res && res.data && Array.isArray(res.data)) {
        self.historyEntries = res.data;
        self.historyTotal = res.total || 0;
      } else {
        var body = res && res.data ? res.data : res;
        if (body && body.payments) {
          // normalize already done in api, but handle fallback
          self.historyEntries = body.payments;
          self.historyTotal = body.total || 0;
        } else {
          self.historyEntries = [];
          self.historyTotal = 0;
        }
      }
      self.loadingHistory = false;
    }, function() {
      self.loadingHistory = false;
      self.historyEntries = [];
    });
  }

  onHistoryPageChange(page: number): void {
    this.historyPage = page;
    this.loadHistory();
  }

  // Solo presentación: páginas totales
  get totalPages(): number {
    if (!this.limit || this.limit <= 0) { return 1; }
    return Math.max(1, Math.ceil(this.total / this.limit));
  }
}



// WEBPACK FOOTER //
// ./src/app/system/maintainer/maintainer-console.component.ts